import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';

import initializeApollo, { addApolloState } from 'apollo-client';

import PageMeta from 'components/layout/PageMeta';

import MainSlider from 'components/main/MainSlider';
import EventsOnMain from 'components/main/EventsOnMain';
import ContentOnMain from 'components/main/ContentOnMain';
import PostsOnMain from 'components/main/PostsOnMain';
import SpeakersOnMain from 'components/main/SpeakersOnMain';

import {
  MainSliderType,
  PostType,
  ContentType,
  EventType,
  GetPostsQueryVariables,
  GetContentsQueryVariables,
  GetEventsQueryVariables,
  GetSpeakersQueryVariables,
  SpeakerType,
} from 'graphql/types';

import { useMainSlider, getMainSliderSsr } from 'hooks/main/useMainSlider';
import { useEvents, getEventsSsr } from 'hooks/event/useEvents';
import { getContentsSsr, useContents } from 'hooks/content/useContents';
import { getPostsSsr, usePosts } from 'hooks/post/usePosts';
import { getSpeakersSsr, useSpeakers } from 'hooks/speaker/useSpeakers';
import { getMetaInfo } from 'utils/helpers';

const EVENTS_LIMIT = 7;
const CONTENT_LIMIT = 6;
const POSTS_LIMIT = 3;
const SPEAKERS_LIMIT = 20;

const defaultEventsFilters: GetEventsQueryVariables = {
  offset: 0,
  limit: EVENTS_LIMIT,
  startDate: dayjs().format('YYYY-MM-DD'),
};

const defaultContentsFilters: GetContentsQueryVariables = {
  offset: 0,
  limit: CONTENT_LIMIT,
};

const defaultPostsFilters: GetPostsQueryVariables = {
  offset: 0,
  limit: POSTS_LIMIT,
  published: true,
};

const defaultSpeakersFilters: GetSpeakersQueryVariables = {
  offset: 0,
  limit: SPEAKERS_LIMIT,
  showOnMainPage: true,
};

export default function Home() {
  const { pathname } = useRouter();
  const { data: sliderData } = useMainSlider();
  const { data: postsData } = usePosts(defaultPostsFilters);
  const { data: contentsData } = useContents(defaultContentsFilters);
  const { data: eventsData, loading: eventsLoading } =
    useEvents(defaultEventsFilters);
  const { data: speakersData } = useSpeakers(defaultSpeakersFilters);

  const slides = sliderData?.mainSlider
    ? (sliderData.mainSlider as MainSliderType[])
    : [];
  const postsList = (postsData?.posts as PostType[]) || [];
  const eventsList = (eventsData?.events as EventType[]) || [];
  const contentsList = (contentsData?.contents as ContentType[]) || [];
  const speakersList = (speakersData?.speakers as SpeakerType[]) || [];

  return (
    <>
      <PageMeta {...getMetaInfo(pathname)} />
      {slides.length > 0 && <MainSlider data={slides} />}
      {postsList.length > 0 && <PostsOnMain posts={postsList} />}
      <ContentOnMain contents={contentsList} />
      <EventsOnMain events={eventsList} eventsLoading={eventsLoading} />
      {speakersList.length > 0 && <SpeakersOnMain speakers={speakersList} />}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const apolloClient = initializeApollo();

  await getMainSliderSsr(apolloClient);

  await getPostsSsr(apolloClient, defaultPostsFilters);

  await getContentsSsr(ctx, apolloClient, defaultContentsFilters);

  await getEventsSsr(apolloClient, defaultEventsFilters);

  await getSpeakersSsr(apolloClient, defaultSpeakersFilters);

  return addApolloState(apolloClient, {
    props: {},
  });
};
